import React from 'react';
import { Table, Typography } from 'antd';
import type {
  TaxDocumentData,
  TaxDocumentExtras,
} from '@/store/apis/taxDocumentsApi';

const { Text } = Typography;

interface DataViewerProps {
  data: TaxDocumentData | null;
  extras: TaxDocumentExtras | null;
}

const DataViewer: React.FC<DataViewerProps> = ({ data, extras }) => {
  if (!data) return <Text type="secondary">No data</Text>;

  const hasSubitems = data.subitems && data.subitems.length > 0;

  const topData = [];

  if (extras?.property) {
    topData.push({
      key: 'property',
      label: 'Property',
      value: extras.property,
    });
  }

  if (extras?.company) {
    topData.push({ key: 'company', label: 'Company', value: extras.company });
  }

  if (data.amount != null) {
    topData.push({
      key: 'total',
      label: <Text strong>Total</Text>,
      value: (
        <Text strong>
          $
          {data.amount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      ),
    });
  }

  if (topData.length === 0 && !hasSubitems) {
    return (
      <Text
        type="secondary"
        style={{ fontSize: 13, marginTop: 8, display: 'block' }}
      >
        No breakdown available
      </Text>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      {topData.length > 0 && (
        <Table
          size="small"
          pagination={false}
          dataSource={topData}
          style={{ marginBottom: hasSubitems ? 24 : 0 }}
          columns={[
            {
              title: 'Label',
              dataIndex: 'label',
              key: 'label',
              width: '40%',
              render: (node: React.ReactNode) => (
                <div style={{ fontSize: 13 }}>{node}</div>
              ),
            },
            {
              title: 'Value',
              dataIndex: 'value',
              key: 'value',
              render: (node: React.ReactNode) => (
                <div style={{ fontSize: 13 }}>{node}</div>
              ),
            },
          ]}
        />
      )}

      {hasSubitems && (
        <div>
          <Text
            strong
            type="secondary"
            style={{ fontSize: 13, marginBottom: 8, display: 'block' }}
          >
            Subitems
          </Text>

          <Table
            size="small"
            pagination={false}
            dataSource={data.subitems!.map((item, i) => ({ key: i, ...item }))}
            columns={[
              {
                title: 'Label',
                dataIndex: 'label',
                key: 'label',
                width: '40%',
                render: (v: string) => (
                  <Text style={{ fontSize: 13 }}>{v}</Text>
                ),
              },
              {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                width: 140,
                render: (v: number | null) =>
                  v != null ? (
                    <Text
                      style={{ fontSize: 13 }}
                      type={v < 0 ? 'danger' : undefined}
                    >
                      $
                      {v.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Text>
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
              },
              {
                title: 'Comment',
                dataIndex: 'comment',
                key: 'comment',
                render: (v: string | null) =>
                  v ? (
                    <Text style={{ fontSize: 13 }}>{v}</Text>
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
              },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default DataViewer;
